/**
 * Anant Smart Vyapar System — Accounting Module
 * ==============================================
 * Full ERP accounting: Chart of Accounts, Vouchers (double-entry),
 * Trial Balance, P&L, Balance Sheet, Day Book, Ledger — with AI suggestions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, FileText, Layers, PieChart, Sparkles, Plus, Trash2, RefreshCw, Zap, Download } from 'lucide-react';
import api from '../../utils/api';

const GROUP_META = {
  asset:     { label: 'Assets',      color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-400/30' },
  liability: { label: 'Liabilities', color: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-400/30' },
  equity:    { label: 'Equity',      color: 'text-indigo-300',  bg: 'bg-indigo-500/10 border-indigo-400/30' },
  income:    { label: 'Income',      color: 'text-sky-300',     bg: 'bg-sky-500/10 border-sky-400/30' },
  expense:   { label: 'Expenses',    color: 'text-amber-300',   bg: 'bg-amber-500/10 border-amber-400/30' },
};

const VTYPE_META = {
  journal:  { label: 'Journal',      icon: '📝' },
  receipt:  { label: 'Receipt',      icon: '💰' },
  payment:  { label: 'Payment',      icon: '💸' },
  contra:   { label: 'Contra',       icon: '🔁' },
  sales:    { label: 'Sales',        icon: '🧾' },
  purchase: { label: 'Purchase',     icon: '🛒' },
  dr_note:  { label: 'Debit Note',   icon: '↩️' },
  cr_note:  { label: 'Credit Note',  icon: '↪️' },
};

const rupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const downloadPdf = async (title, workspaceName, subtitle, columns, rows, filename, foot) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF();
  doc.setFontSize(16); doc.text(title, 14, 18);
  doc.setFontSize(10); doc.text(workspaceName, 14, 25);
  if (subtitle) { doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(subtitle, 14, 32); doc.setTextColor(0, 0, 0); }
  autoTable(doc, {
    startY: 38, head: [columns], body: rows,
    foot: foot ? [foot] : undefined,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 70, 229] },
    footStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
  });
  const y = (doc.lastAutoTable?.finalY || 60) + 8;
  doc.setFontSize(8); doc.setTextColor(120, 120, 120);
  doc.text('Generated by Anant Smart Vyapar System — for reconciliation only.', 14, y);
  doc.save(filename);
};

// ============ Chart of Accounts ============
const COATab = ({ workspace, accounts, reload }) => {
  const [form, setForm] = useState({ name: '', group: 'expense', sub_type: '', code: '', opening_balance: '' });
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const addAcc = async () => {
    if (!form.name.trim()) return toast.error('Naam daalein');
    setBusy(true);
    try {
      await api.post('/vyapar/acc/coa', { ...form, workspace_id: workspace.id, opening_balance: parseFloat(form.opening_balance) || 0 });
      toast.success('✅ Account created');
      setForm({ name: '', group: 'expense', sub_type: '', code: '', opening_balance: '' });
      reload();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to add account';
      toast.error(msg, { duration: 6000 });
    }
    finally { setBusy(false); }
  };

  const parseCsv = (text) => {
    // Simple CSV parser — supports quoted fields with commas
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const splitLine = (l) => {
      const out = []; let cur = ''; let inQ = false;
      for (const ch of l) {
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === ',' && !inQ) { out.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      out.push(cur.trim()); return out;
    };
    const headers = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).map((l) => {
      const cells = splitLine(l);
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] ?? ''; });
      return {
        code: row.code || '',
        name: row.name || '',
        group: row.group || row.type || '',
        sub_type: row.sub_type || row.subtype || '',
        opening_balance: parseFloat(row.opening_balance || row.opening || '0') || 0,
        notes: row.notes || '',
      };
    }).filter((r) => r.name);
  };

  const doImport = async (rows) => {
    if (!rows.length) { toast.error('CSV me koi valid rows nahi mile'); return; }
    if (!window.confirm(`Import ${rows.length} accounts from CSV?\n\nDuplicates (same name+group) will be skipped.`)) return;
    setImporting(true);
    try {
      const r = await api.post('/vyapar/acc/coa/bulk-import', {
        workspace_id: workspace.id, rows, skip_duplicates: true,
      });
      setImportResult(r.data);
      toast.success(`✅ ${r.data.inserted_count} added · ${r.data.skipped_count} skipped · ${r.data.error_count} errors`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed', { duration: 6000 });
    } finally { setImporting(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { doImport(parseCsv(String(reader.result || ''))); };
    reader.readAsText(f);
    e.target.value = '';  // reset so same file can be re-picked
  };

  const downloadTemplate = () => {
    const csv = 'code,name,group,sub_type,opening_balance,notes\n' +
      '1000,Cash in Hand,asset,cash,0,\n' +
      '1010,HDFC Bank,asset,bank,50000,Current A/c\n' +
      ',Tea Expense,expense,,0,Auto-code will be assigned\n' +
      ',Coffee Expense,expense,,0,\n' +
      '4000,Sales Income,income,sales,0,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'anant-coa-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try { await api.delete(`/vyapar/acc/coa/${id}?workspace_id=${workspace.id}`); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const grouped = useMemo(() => {
    const g = { asset: [], liability: [], equity: [], income: [], expense: [] };
    accounts.forEach((a) => { if (g[a.group]) g[a.group].push(a); });
    return g;
  }, [accounts]);
  return (
    <div className="space-y-3" data-testid="acc-coa-tab">
      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
        <div className="text-xs font-bold mb-2 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add New Account</div>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
          <input placeholder="Account Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="acc-new-name" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} data-testid="acc-new-group" className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
            {Object.entries(GROUP_META).map(([k, v]) => <option key={k} value={k} className="bg-slate-900">{v.label}</option>)}
          </select>
          <input placeholder="Code (auto)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <input type="number" placeholder="Opening ₹" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <button onClick={addAcc} disabled={busy} data-testid="acc-new-save" className="py-1.5 rounded-lg bg-emerald-500 text-white text-sm font-bold disabled:opacity-60">{busy ? '…' : 'Add'}</button>
        </div>

        {/* 📥 CSV Bulk Import */}
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <div className="text-[11px] font-black text-cyan-300 uppercase tracking-widest">📥 CSV Bulk Import (Tally/Marg migration)</div>
              <div className="text-[10px] text-slate-400">Columns: <code className="text-amber-300">code, name, group, sub_type, opening_balance, notes</code> · Groups: asset/liability/equity/income/expense · Auto-code if empty</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadTemplate}
                data-testid="coa-download-template"
                className="px-2.5 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs font-black hover:bg-white/20"
              >⬇ Template</button>
              <input
                ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile}
                data-testid="coa-import-file" className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="coa-import-btn"
                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-xs font-black disabled:opacity-60"
              >{importing ? '⏳ Importing...' : '📥 Import CSV'}</button>
            </div>
          </div>

          {importResult && (
            <div className="mt-2 rounded-lg border border-white/10 bg-black/40 p-2 text-[11px]" data-testid="coa-import-result">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-black">✅ {importResult.inserted_count} added</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-black">⚠ {importResult.skipped_count} skipped</span>
                <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-black">✗ {importResult.error_count} errors</span>
                <button onClick={() => setImportResult(null)} className="ml-auto text-slate-400 text-[10px]">Dismiss</button>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-1 max-h-24 overflow-auto text-[10px] text-red-300">
                  {importResult.errors.slice(0, 10).map((e, i) => (
                    <div key={i}>Row {e.row}: {e.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {Object.entries(grouped).map(([g, arr]) => arr.length > 0 && (
        <div key={g} className={`rounded-xl border p-3 ${GROUP_META[g].bg}`}>
          <div className={`text-xs font-bold mb-2 ${GROUP_META[g].color} uppercase tracking-wider`}>{GROUP_META[g].label} ({arr.length})</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {arr.map((a) => (
              <div key={a.id} className="flex items-center justify-between bg-black/20 rounded-lg px-2 py-1.5">
                <div className="min-w-0">
                  <div className="text-xs font-bold truncate">{a.code} · {a.name}</div>
                  <div className="text-[10px] text-slate-400">{a.sub_type || '—'} · Balance: <span className="font-mono text-emerald-300">{rupee(a.balance)}</span></div>
                </div>
                <button onClick={() => del(a.id)} className="text-red-300 p-1" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Vouchers Tab ============
const VouchersTab = ({ workspace, accounts, reload }) => {
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState({
    voucher_type: 'receipt',
    date: new Date().toISOString().slice(0, 10),
    narration: '',
    reference: '',
    lines: [{ account_id: '', dr: '', cr: '' }, { account_id: '', dr: '', cr: '' }],
    // GST
    hsn_code: '',
    gst_rate: 0,
    taxable_value: '',
    inter_state: false,
  });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [hsn, setHsn] = useState([]);
  useEffect(() => { api.get(`/vyapar/gst/config?workspace_id=${workspace.id}`).then((r) => setHsn(r.data.hsn_catalog || [])).catch(() => {}); }, [workspace.id]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/vyapar/acc/vouchers?workspace_id=${workspace.id}&limit=100`);
      setVouchers(r.data.vouchers || []);
    } catch { /* ignore */ }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  const drSum = form.lines.reduce((s, l) => s + (parseFloat(l.dr) || 0), 0);
  const crSum = form.lines.reduce((s, l) => s + (parseFloat(l.cr) || 0), 0);
  const balanced = Math.abs(drSum - crSum) < 0.01 && drSum > 0;

  const updateLine = (i, field, val) => {
    const nl = [...form.lines];
    nl[i] = { ...nl[i], [field]: val };
    setForm({ ...form, lines: nl });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_id: '', dr: '', cr: '' }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const aiSuggest = async () => {
    if (!form.narration.trim()) return toast.error('Narration daalein');
    setAiBusy(true);
    try {
      const r = await api.post('/vyapar/acc/ai/suggest-account', { workspace_id: workspace.id, narration: form.narration, amount: drSum || crSum });
      const s = r.data.suggestion;
      if (s) {
        toast.success(`💡 AI suggests: ${s.code} · ${s.name}`);
        // Fill first empty line
        const idx = form.lines.findIndex((l) => !l.account_id);
        if (idx >= 0) updateLine(idx, 'account_id', s.account_id);
      } else {
        toast('No confident match — please select manually');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(false); }
  };

  // GST auto-calc
  const isGstVoucher = ['sales', 'purchase', 'dr_note', 'cr_note'].includes(form.voucher_type);
  const gstCalc = useMemo(() => {
    const tv = parseFloat(form.taxable_value) || 0;
    const rate = parseFloat(form.gst_rate) || 0;
    const total = Math.round(tv * rate) / 100;
    const half = Math.round(total * 50) / 100;
    return form.inter_state
      ? { cgst: 0, sgst: 0, igst: total, total, grand: tv + total }
      : { cgst: half, sgst: total - half, igst: 0, total, grand: tv + total };
  }, [form.taxable_value, form.gst_rate, form.inter_state]);

  const submit = async () => {
    if (!balanced) return toast.error(`Dr (${rupee(drSum)}) ≠ Cr (${rupee(crSum)}). Balance karein.`);
    if (form.lines.some((l) => !l.account_id)) return toast.error('Sab lines ka account chunein');
    setBusy(true);
    try {
      const payload = {
        workspace_id: workspace.id,
        voucher_type: form.voucher_type,
        date: form.date,
        narration: form.narration,
        reference: form.reference,
        lines: form.lines.map((l) => ({ account_id: l.account_id, dr: parseFloat(l.dr) || 0, cr: parseFloat(l.cr) || 0, narration: '' })),
      };
      if (isGstVoucher && (parseFloat(form.taxable_value) || 0) > 0) {
        payload.hsn_code = form.hsn_code;
        payload.gst_rate = parseFloat(form.gst_rate) || 0;
        payload.taxable_value = parseFloat(form.taxable_value) || 0;
        payload.cgst = gstCalc.cgst;
        payload.sgst = gstCalc.sgst;
        payload.igst = gstCalc.igst;
        payload.inter_state = form.inter_state;
      }
      await api.post('/vyapar/acc/vouchers', payload);
      toast.success('✅ Voucher saved · double-entry posted');
      setForm({ ...form, narration: '', reference: '', taxable_value: '', hsn_code: '', lines: [{ account_id: '', dr: '', cr: '' }, { account_id: '', dr: '', cr: '' }] });
      load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete voucher? (Ledger reverse ho jayega)')) return;
    try { await api.delete(`/vyapar/acc/vouchers/${id}?workspace_id=${workspace.id}`); toast.success('Deleted'); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="acc-vouchers-tab">
      {accounts.length === 0 && (
        <div className="p-3 rounded-xl bg-amber-500/15 border border-amber-400/40 text-amber-200 text-xs">
          ⚠️ Pehle &quot;Chart of Accounts&quot; me accounts create karein ya seed karein.
        </div>
      )}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-slate-900/60 to-fuchsia-950/30 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-fuchsia-300" /><h3 className="text-sm font-bold">➕ New Voucher</h3></div>
          <button onClick={aiSuggest} disabled={aiBusy} data-testid="acc-ai-suggest" className="text-[10px] px-2 py-1 rounded-lg bg-purple-500/25 text-purple-200 border border-purple-400/40 font-bold flex items-center gap-1 disabled:opacity-60">
            <Sparkles className="w-3 h-3" /> {aiBusy ? 'Sochta hoon…' : 'AI Suggest Account'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
          <select value={form.voucher_type} onChange={(e) => setForm({ ...form, voucher_type: e.target.value })} data-testid="acc-vtype" className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
            {Object.entries(VTYPE_META).map(([k, v]) => <option key={k} value={k} className="bg-slate-900">{v.icon} {v.label}</option>)}
          </select>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <input placeholder="Reference no." value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
          <input placeholder="Narration" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} data-testid="acc-narration" className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
        </div>
        {isGstVoucher && (
          <div className="mt-2 rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-2" data-testid="acc-gst-panel">
            <div className="text-[10px] uppercase font-bold text-emerald-300 mb-1.5">📋 GST Details (Sales/Purchase/Notes)</div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
              <select value={form.hsn_code} onChange={(e) => {
                const h = hsn.find((x) => x.code === e.target.value);
                setForm({ ...form, hsn_code: e.target.value, gst_rate: h ? h.gst : form.gst_rate });
              }} data-testid="acc-hsn" className="col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs">
                <option value="">-- HSN / SAC (optional) --</option>
                {hsn.map((h) => <option key={h.code} value={h.code} className="bg-slate-900">{h.code} · {h.desc} ({h.gst}%)</option>)}
              </select>
              <select value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: parseFloat(e.target.value) })} data-testid="acc-gst-rate" className="bg-white/5 border border-emerald-400/40 rounded px-2 py-1.5 text-xs">
                {[0, 3, 5, 12, 18, 28].map((r) => <option key={r} value={r} className="bg-slate-900">GST {r}%</option>)}
              </select>
              <input type="number" placeholder="Taxable ₹" value={form.taxable_value} onChange={(e) => setForm({ ...form, taxable_value: e.target.value })} data-testid="acc-taxable" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
              <label className="flex items-center gap-1 text-[11px] text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.inter_state} onChange={(e) => setForm({ ...form, inter_state: e.target.checked })} data-testid="acc-inter-state" className="rounded" /> Inter-state (IGST)
              </label>
              <div className="text-[10px] text-slate-400 font-mono self-center">
                {form.inter_state ? <>IGST: <b className="text-emerald-300">{rupee(gstCalc.igst)}</b></> : <>CGST+SGST: <b className="text-emerald-300">{rupee(gstCalc.total)}</b></>} · Total: <b className="text-fuchsia-300">{rupee(gstCalc.grand)}</b>
              </div>
            </div>
          </div>
        )}
        <div className="mt-2 space-y-1">
          {form.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
              <select value={l.account_id} onChange={(e) => updateLine(i, 'account_id', e.target.value)} data-testid={`acc-line-${i}-account`} className="col-span-6 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs">
                <option value="">-- Account chunein --</option>
                {accounts.map((a) => <option key={a.id} value={a.id} className="bg-slate-900">{a.code} · {a.name} [{a.group}]</option>)}
              </select>
              <input type="number" placeholder="Dr" value={l.dr} onChange={(e) => updateLine(i, 'dr', e.target.value)} data-testid={`acc-line-${i}-dr`} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
              <input type="number" placeholder="Cr" value={l.cr} onChange={(e) => updateLine(i, 'cr', e.target.value)} data-testid={`acc-line-${i}-cr`} className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
              <button onClick={() => removeLine(i)} disabled={form.lines.length <= 2} className="col-span-2 text-red-300 disabled:opacity-30 flex justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <button onClick={addLine} className="text-xs px-2 py-1 rounded bg-white/10 text-slate-300">+ Add Line</button>
          <div className="text-xs flex items-center gap-3">
            <span>Dr: <b className="text-emerald-300">{rupee(drSum)}</b></span>
            <span>Cr: <b className="text-rose-300">{rupee(crSum)}</b></span>
            <span className={balanced ? 'text-emerald-300 font-bold' : 'text-amber-300 font-bold'}>{balanced ? '✓ Balanced' : '⚠ Unbalanced'}</span>
          </div>
          <button onClick={submit} disabled={busy || !balanced} data-testid="acc-voucher-save" className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-sm font-bold disabled:opacity-40">{busy ? 'Saving…' : 'Save Voucher'}</button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">No.</th><th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Narration</th><th className="text-right px-3 py-2">Amount</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {vouchers.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi voucher nahi. Upar se add karein.</td></tr>}
            {vouchers.map((v) => (
              <tr key={v.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 text-xs">{v.date}</td>
                <td className="px-3 py-2 font-mono text-xs text-fuchsia-300">{v.voucher_no}</td>
                <td className="px-3 py-2 text-xs">{VTYPE_META[v.voucher_type]?.icon} {VTYPE_META[v.voucher_type]?.label}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[240px]">{v.narration || '-'}</td>
                <td className="px-3 py-2 text-right font-bold">{rupee(v.total_amount)}</td>
                <td className="px-3 py-2 text-center"><button onClick={() => del(v.id)} className="p-1 text-red-300" title="Delete + reverse"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ Reports Tab ============
const ReportsTab = ({ workspace }) => {
  const [tb, setTb] = useState(null);
  const [pl, setPl] = useState(null);
  const [bs, setBs] = useState(null);
  const [db, setDb] = useState(null);
  const [gst, setGst] = useState(null);
  const [rep, setRep] = useState('tb');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [asOf, setAsOf] = useState('');
  const [dbDate, setDbDate] = useState(new Date().toISOString().slice(0, 10));

  const runReport = useCallback(async () => {
    try {
      if (rep === 'tb') {
        const r = await api.get(`/vyapar/acc/reports/trial-balance?workspace_id=${workspace.id}${asOf ? `&as_of=${asOf}` : ''}`);
        setTb(r.data);
      } else if (rep === 'pl') {
        const q = new URLSearchParams({ workspace_id: workspace.id });
        if (from) q.append('from_date', from);
        if (to) q.append('to_date', to);
        const r = await api.get(`/vyapar/acc/reports/profit-loss?${q}`);
        setPl(r.data);
      } else if (rep === 'bs') {
        const r = await api.get(`/vyapar/acc/reports/balance-sheet?workspace_id=${workspace.id}${asOf ? `&as_of=${asOf}` : ''}`);
        setBs(r.data);
      } else if (rep === 'db') {
        const r = await api.get(`/vyapar/acc/reports/day-book?workspace_id=${workspace.id}&date=${dbDate}`);
        setDb(r.data);
      } else if (rep === 'gst') {
        const q = new URLSearchParams({ workspace_id: workspace.id });
        if (from) q.append('from_date', from);
        if (to) q.append('to_date', to);
        const r = await api.get(`/vyapar/gst/reports/summary?${q}`);
        setGst(r.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  }, [rep, workspace.id, asOf, from, to, dbDate]);
  useEffect(() => { runReport(); }, [runReport]);

  const REPS = [
    { id: 'tb', label: 'Trial Balance', icon: '⚖️' },
    { id: 'pl', label: 'Profit & Loss', icon: '📈' },
    { id: 'bs', label: 'Balance Sheet', icon: '📊' },
    { id: 'db', label: 'Day Book',      icon: '📆' },
    { id: 'gst', label: 'GST Summary',  icon: '🧾' },
  ];

  const downloadGstr = async (kind) => {
    try {
      const q = new URLSearchParams({ workspace_id: workspace.id });
      if (from) q.append('from_date', from);
      if (to) q.append('to_date', to);
      const r = await api.get(`/vyapar/gst/reports/${kind}?${q}`);
      const wsn = workspace.business_name;
      if (kind === 'gstr1') {
        const rows = r.data.rows || [];
        const header = 'VoucherNo,Date,Party,HSN,Taxable,GST%,CGST,SGST,IGST,Total\n';
        const csv = header + rows.map((x) => [x.voucher_no, x.date, `"${x.party}"`, x.hsn, x.taxable_value, x.gst_rate, x.cgst, x.sgst, x.igst, x.total].join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `GSTR-1-${wsn}.csv`.replace(/\s+/g, '_'); a.click();
        toast.success('GSTR-1 CSV downloaded');
      } else if (kind === 'gstr3b') {
        const d = r.data;
        const header = 'Section,Taxable,CGST,SGST,IGST\n';
        const csv = header +
          `3.1 Outward Supplies,${d['3.1_outward'].taxable},${d['3.1_outward'].cgst},${d['3.1_outward'].sgst},${d['3.1_outward'].igst}\n` +
          `4 ITC Available,${d['4_itc'].taxable},${d['4_itc'].cgst},${d['4_itc'].sgst},${d['4_itc'].igst}\n` +
          `6.1 Net Payable,,,,${d['6.1_net_payable']}\n`;
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `GSTR-3B-${wsn}.csv`.replace(/\s+/g, '_'); a.click();
        toast.success('GSTR-3B CSV downloaded');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportPdf = () => {
    const wsn = workspace.business_name;
    if (rep === 'tb' && tb) {
      downloadPdf('TRIAL BALANCE', wsn, `As of ${tb.as_of}`,
        ['Code', 'Account', 'Group', 'Dr (₹)', 'Cr (₹)'],
        tb.rows.map((r) => [r.code, r.name, r.group, r.dr.toLocaleString('en-IN'), r.cr.toLocaleString('en-IN')]),
        `trial-balance-${wsn}-${tb.as_of}.pdf`,
        ['', '', 'TOTAL', tb.total_dr.toLocaleString('en-IN'), tb.total_cr.toLocaleString('en-IN')]);
    } else if (rep === 'pl' && pl) {
      const rows = [];
      rows.push(['— INCOME —', '', '']);
      pl.income.forEach((r) => rows.push([r.code, r.name, r.amount.toLocaleString('en-IN')]));
      rows.push(['', 'Total Income', pl.total_income.toLocaleString('en-IN')]);
      rows.push(['— EXPENSES —', '', '']);
      pl.expense.forEach((r) => rows.push([r.code, r.name, r.amount.toLocaleString('en-IN')]));
      rows.push(['', 'Total Expenses', pl.total_expense.toLocaleString('en-IN')]);
      downloadPdf('PROFIT & LOSS STATEMENT', wsn,
        `${pl.from_date || 'All-time'} → ${pl.to_date || 'Today'}`,
        ['Code', 'Account', 'Amount (₹)'], rows,
        `pnl-${wsn}.pdf`,
        ['', 'NET PROFIT', pl.net_profit.toLocaleString('en-IN')]);
    } else if (rep === 'bs' && bs) {
      const rows = [];
      rows.push(['— ASSETS —', '', '']);
      bs.assets.forEach((r) => rows.push([r.code, r.name, r.amount.toLocaleString('en-IN')]));
      rows.push(['', 'Total Assets', bs.total_assets.toLocaleString('en-IN')]);
      rows.push(['— LIABILITIES —', '', '']);
      bs.liabilities.forEach((r) => rows.push([r.code, r.name, r.amount.toLocaleString('en-IN')]));
      rows.push(['', 'Total Liabilities', bs.total_liabilities.toLocaleString('en-IN')]);
      rows.push(['— EQUITY —', '', '']);
      bs.equity.forEach((r) => rows.push([r.code, r.name, r.amount.toLocaleString('en-IN')]));
      rows.push(['', 'Total Equity', bs.total_equity.toLocaleString('en-IN')]);
      downloadPdf('BALANCE SHEET', wsn, `As of ${bs.as_of}`,
        ['Code', 'Account', 'Amount (₹)'], rows,
        `balance-sheet-${wsn}-${bs.as_of}.pdf`);
    } else if (rep === 'db' && db) {
      const rows = [];
      db.vouchers.forEach((v) => {
        rows.push([v.voucher_no, VTYPE_META[v.voucher_type]?.label, v.narration || '-', v.total_amount.toLocaleString('en-IN')]);
      });
      downloadPdf('DAY BOOK', wsn, `Date: ${db.date}`,
        ['Voucher', 'Type', 'Narration', 'Amount (₹)'], rows, `day-book-${wsn}-${db.date}.pdf`);
    }
  };

  return (
    <div className="space-y-3" data-testid="acc-reports-tab">
      <div className="flex gap-1 flex-wrap">
        {REPS.map((r) => (
          <button key={r.id} onClick={() => setRep(r.id)} data-testid={`acc-rep-${r.id}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${rep === r.id ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300'}`}>{r.icon} {r.label}</button>
        ))}
        <button onClick={exportPdf} data-testid="acc-report-pdf" className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500 text-white flex items-center gap-1"><Download className="w-3 h-3" /> PDF</button>
      </div>

      {(rep === 'tb' || rep === 'bs') && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">As of:</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
        </div>
      )}
      {rep === 'pl' && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-400">From:</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
          <label className="text-xs text-slate-400">To:</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
        </div>
      )}
      {rep === 'gst' && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-400">From:</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
          <label className="text-xs text-slate-400">To:</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" />
          <button onClick={() => downloadGstr('gstr1')} data-testid="acc-gstr1-btn" className="text-xs px-2 py-1 rounded bg-emerald-500 text-white font-bold">📄 GSTR-1 CSV</button>
          <button onClick={() => downloadGstr('gstr3b')} data-testid="acc-gstr3b-btn" className="text-xs px-2 py-1 rounded bg-indigo-500 text-white font-bold">📄 GSTR-3B CSV</button>
        </div>
      )}
      {rep === 'db' && (
        <div className="flex items-center gap-2"><label className="text-xs text-slate-400">Date:</label><input type="date" value={dbDate} onChange={(e) => setDbDate(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" /></div>
      )}

      {rep === 'tb' && tb && (
        <div className="rounded-xl border border-white/10 overflow-x-auto">
          <div className={`p-2 text-xs font-bold text-center ${tb.in_balance ? 'text-emerald-300' : 'text-red-300'}`}>{tb.in_balance ? '✓ Trial Balance is Balanced' : '⚠ Not Balanced'} · Total Dr: {rupee(tb.total_dr)} · Total Cr: {rupee(tb.total_cr)}</div>
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Account</th><th className="text-left px-3 py-2">Group</th><th className="text-right px-3 py-2">Dr</th><th className="text-right px-3 py-2">Cr</th></tr></thead>
            <tbody>
              {tb.rows.filter((r) => r.dr !== 0 || r.cr !== 0).map((r) => (
                <tr key={r.account_id} className="border-t border-white/5">
                  <td className="px-3 py-1.5 font-mono text-xs">{r.code}</td>
                  <td className="px-3 py-1.5 text-xs">{r.name}</td>
                  <td className={`px-3 py-1.5 text-[10px] uppercase ${GROUP_META[r.group]?.color}`}>{r.group}</td>
                  <td className="px-3 py-1.5 text-right text-xs font-mono">{r.dr ? rupee(r.dr) : '-'}</td>
                  <td className="px-3 py-1.5 text-right text-xs font-mono">{r.cr ? rupee(r.cr) : '-'}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-white/20 bg-white/5 font-bold"><td colSpan={3} className="px-3 py-2 text-right">TOTAL</td><td className="px-3 py-2 text-right font-mono">{rupee(tb.total_dr)}</td><td className="px-3 py-2 text-right font-mono">{rupee(tb.total_cr)}</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {rep === 'pl' && pl && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-sky-400/30 bg-sky-500/5 p-3">
            <div className="text-sm font-bold text-sky-300 mb-2">Income</div>
            {pl.income.length === 0 && <div className="text-xs text-slate-500">No income entries</div>}
            {pl.income.map((r) => <div key={r.account_id} className="flex justify-between text-xs py-1"><span>{r.code} · {r.name}</span><span className="font-mono">{rupee(r.amount)}</span></div>)}
            <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-sm font-bold"><span>Total Income</span><span className="text-sky-300">{rupee(pl.total_income)}</span></div>
          </div>
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-3">
            <div className="text-sm font-bold text-amber-300 mb-2">Expenses</div>
            {pl.expense.length === 0 && <div className="text-xs text-slate-500">No expense entries</div>}
            {pl.expense.map((r) => <div key={r.account_id} className="flex justify-between text-xs py-1"><span>{r.code} · {r.name}</span><span className="font-mono">{rupee(r.amount)}</span></div>)}
            <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-sm font-bold"><span>Total Expenses</span><span className="text-amber-300">{rupee(pl.total_expense)}</span></div>
          </div>
          <div className="md:col-span-2 rounded-xl border border-emerald-400/50 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 p-4 text-center">
            <div className="text-xs uppercase tracking-widest text-emerald-200">Net {pl.net_profit >= 0 ? 'Profit' : 'Loss'}</div>
            <div className={`text-3xl font-extrabold ${pl.net_profit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{rupee(pl.net_profit)}</div>
          </div>
        </div>
      )}

      {rep === 'bs' && bs && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-3">
            <div className="text-sm font-bold text-emerald-300 mb-2">Assets</div>
            {bs.assets.map((r) => <div key={r.account_id} className="flex justify-between text-xs py-1"><span>{r.code} · {r.name}</span><span className="font-mono">{rupee(r.amount)}</span></div>)}
            <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-sm font-bold"><span>Total Assets</span><span className="text-emerald-300">{rupee(bs.total_assets)}</span></div>
          </div>
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/5 p-3">
            <div className="text-sm font-bold text-rose-300 mb-2">Liabilities + Equity</div>
            {bs.liabilities.map((r) => <div key={r.account_id} className="flex justify-between text-xs py-1"><span>{r.code} · {r.name}</span><span className="font-mono">{rupee(r.amount)}</span></div>)}
            {bs.equity.map((r) => <div key={r.account_id} className="flex justify-between text-xs py-1"><span>{r.code} · {r.name}</span><span className="font-mono">{rupee(r.amount)}</span></div>)}
            <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-sm font-bold"><span>Total L + E</span><span className="text-rose-300">{rupee(bs.total_liabilities + bs.total_equity)}</span></div>
          </div>
          <div className={`md:col-span-2 rounded-xl border p-2 text-center text-xs font-bold ${bs.in_balance ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-300' : 'border-red-400/50 bg-red-500/10 text-red-300'}`}>
            {bs.in_balance ? '✓ Balance Sheet is Balanced' : '⚠ Not Balanced — check journal entries'}
          </div>
        </div>
      )}

      {rep === 'db' && db && (
        <div className="rounded-xl border border-white/10 overflow-x-auto">
          <div className="p-2 text-xs text-slate-400">Day Book · {db.date} · {db.count} vouchers</div>
          {db.vouchers.length === 0 && <div className="p-4 text-center text-slate-500 text-xs">Is date me koi voucher nahi</div>}
          {db.vouchers.map((v) => (
            <div key={v.id} className="border-t border-white/5 p-2 hover:bg-white/5">
              <div className="flex justify-between text-xs">
                <div><span className="font-mono text-fuchsia-300">{v.voucher_no}</span> · {VTYPE_META[v.voucher_type]?.icon} {VTYPE_META[v.voucher_type]?.label}</div>
                <div className="font-bold">{rupee(v.total_amount)}</div>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{v.narration}</div>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {(v.lines || []).map((l, i) => (
                  <div key={i} className="text-[10px] flex justify-between bg-black/20 rounded px-1.5 py-0.5">
                    <span className="truncate">{l.account_code} {l.account_name}</span>
                    <span className={l.dr > 0 ? 'text-emerald-300' : 'text-rose-300'}>{l.dr > 0 ? `Dr ${rupee(l.dr)}` : `Cr ${rupee(l.cr)}`}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {rep === 'gst' && gst && (
        <div className="space-y-3" data-testid="acc-gst-report">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-sky-400/30 bg-sky-500/5 p-3">
              <div className="text-[10px] uppercase font-bold text-sky-300">Output GST (Sales)</div>
              <div className="text-2xl font-extrabold text-sky-200">{rupee(gst.output_gst)}</div>
              <div className="text-[10px] text-slate-400">Taxable: {rupee(gst.outward.totals.taxable)} · {gst.outward.totals.count} bills</div>
            </div>
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-3">
              <div className="text-[10px] uppercase font-bold text-amber-300">Input GST (Purchase / ITC)</div>
              <div className="text-2xl font-extrabold text-amber-200">{rupee(gst.input_gst)}</div>
              <div className="text-[10px] text-slate-400">Taxable: {rupee(gst.inward.totals.taxable)} · {gst.inward.totals.count} bills</div>
            </div>
            <div className={`rounded-xl border p-3 ${gst.net_payable >= 0 ? 'border-red-400/40 bg-red-500/10' : 'border-emerald-400/40 bg-emerald-500/10'}`}>
              <div className="text-[10px] uppercase font-bold">Net GST Payable</div>
              <div className={`text-2xl font-extrabold ${gst.net_payable >= 0 ? 'text-red-200' : 'text-emerald-200'}`}>{rupee(gst.net_payable)}</div>
              <div className="text-[10px] text-slate-400">{gst.net_payable >= 0 ? 'Pay to govt' : 'Refund / Carry-fwd'}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-xs font-bold mb-2">Outward — GST Rate Wise</div>
              <table className="w-full text-xs"><thead className="text-slate-400"><tr><th className="text-left">Rate</th><th className="text-right">Taxable</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th></tr></thead><tbody>
                {gst.outward.rows.length === 0 && <tr><td colSpan={5} className="py-2 text-center text-slate-500">Koi sales GST entry nahi</td></tr>}
                {gst.outward.rows.map((r) => <tr key={String(r._id)} className="border-t border-white/5"><td>{r._id ?? 0}%</td><td className="text-right">{rupee(r.taxable)}</td><td className="text-right">{rupee(r.cgst)}</td><td className="text-right">{rupee(r.sgst)}</td><td className="text-right">{rupee(r.igst)}</td></tr>)}
              </tbody></table>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-xs font-bold mb-2">Inward — GST Rate Wise</div>
              <table className="w-full text-xs"><thead className="text-slate-400"><tr><th className="text-left">Rate</th><th className="text-right">Taxable</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th></tr></thead><tbody>
                {gst.inward.rows.length === 0 && <tr><td colSpan={5} className="py-2 text-center text-slate-500">Koi purchase GST entry nahi</td></tr>}
                {gst.inward.rows.map((r) => <tr key={String(r._id)} className="border-t border-white/5"><td>{r._id ?? 0}%</td><td className="text-right">{rupee(r.taxable)}</td><td className="text-right">{rupee(r.cgst)}</td><td className="text-right">{rupee(r.sgst)}</td><td className="text-right">{rupee(r.igst)}</td></tr>)}
              </tbody></table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const DashKpis = ({ workspace }) => {
  const [kpi, setKpi] = useState(null);
  const [gst, setGst] = useState(null);
  useEffect(() => {
    api.get(`/vyapar/acc/dashboard/kpis?workspace_id=${workspace.id}`).then((r) => setKpi(r.data)).catch(() => {});
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
    api.get(`/vyapar/gst/reports/summary?workspace_id=${workspace.id}&from_date=${from}&to_date=${to}`).then((r) => setGst(r.data)).catch(() => {});
  }, [workspace.id]);
  if (!kpi) return null;
  const cards = [
    { label: 'Income (MTD)',       val: rupee(kpi.income_mtd),        cls: 'from-sky-500/20 to-sky-600/20 border-sky-400/30 text-sky-200' },
    { label: 'Expense (MTD)',      val: rupee(kpi.expense_mtd),       cls: 'from-amber-500/20 to-orange-500/20 border-amber-400/30 text-amber-200' },
    { label: 'Profit (MTD)',       val: rupee(kpi.profit_mtd),        cls: 'from-emerald-500/20 to-teal-500/20 border-emerald-400/30 text-emerald-200' },
    { label: 'Cash + Bank',        val: rupee(kpi.cash_bank_balance), cls: 'from-fuchsia-500/20 to-pink-500/20 border-fuchsia-400/30 text-fuchsia-200' },
    { label: 'Receivables',        val: rupee(kpi.receivable),        cls: 'from-indigo-500/20 to-blue-500/20 border-indigo-400/30 text-indigo-200' },
    { label: 'Payables',           val: rupee(kpi.payable),           cls: 'from-rose-500/20 to-red-500/20 border-rose-400/30 text-rose-200' },
    { label: 'GST Payable (MTD)',  val: rupee(gst ? gst.net_payable : 0), cls: 'from-red-500/20 to-orange-500/20 border-red-400/30 text-red-200' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-3" data-testid="acc-kpis">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-xl border bg-gradient-to-br p-3 ${c.cls}`}>
          <div className="text-[10px] uppercase font-bold opacity-80">{c.label}</div>
          <div className="text-lg font-extrabold mt-0.5">{c.val}</div>
        </div>
      ))}
    </div>
  );
};

// ============ Main Wrapper ============
const AccountingTab = ({ workspace }) => {
  const [sub, setSub] = useState('coa');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get(`/vyapar/acc/coa?workspace_id=${workspace.id}`); setAccounts(r.data.accounts || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    try { const r = await api.post(`/vyapar/acc/coa/seed?workspace_id=${workspace.id}`); toast.success(`✅ ${r.data.inserted} default accounts added`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const SUBS = [
    { id: 'dash',    label: 'Dashboard',   icon: PieChart },
    { id: 'coa',     label: 'Chart of A/c',icon: Layers },
    { id: 'voucher', label: 'Vouchers',    icon: FileText },
    { id: 'reports', label: 'Reports',     icon: BookOpen },
  ];

  return (
    <div className="space-y-3" data-testid="acc-main">
      <div className="rounded-2xl border border-indigo-400/30 bg-gradient-to-br from-indigo-950/60 to-slate-900/60 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-bold text-indigo-300 uppercase tracking-widest">🏛️ Smart Accounting ERP</div>
            <div className="text-[11px] text-slate-400">Chart of Accounts · Vouchers · Trial Balance · P&L · Balance Sheet · Day Book</div>
          </div>
          {!loading && accounts.length === 0 && (
            <button onClick={seed} data-testid="acc-seed-btn" className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-bold flex items-center gap-1"><Zap className="w-3 h-3" /> Seed Default Accounts (19)</button>
          )}
          <button onClick={load} className="text-xs px-2 py-1 rounded bg-white/10 text-slate-300" title="Reload"><RefreshCw className="w-3 h-3" /></button>
        </div>
        <div className="flex gap-1 mt-3 overflow-x-auto">
          {SUBS.map((s) => {
            const Ic = s.icon;
            return (
              <button key={s.id} onClick={() => setSub(s.id)} data-testid={`acc-subtab-${s.id}`} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${sub === s.id ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>
                <Ic className="w-3 h-3" />{s.label}
              </button>
            );
          })}
        </div>
      </div>

      {sub === 'dash' && <><DashKpis workspace={workspace} /><ReportsTab workspace={workspace} /></>}
      {sub === 'coa' && <COATab workspace={workspace} accounts={accounts} reload={load} />}
      {sub === 'voucher' && <VouchersTab workspace={workspace} accounts={accounts} reload={load} />}
      {sub === 'reports' && <ReportsTab workspace={workspace} />}
    </div>
  );
};

export default AccountingTab;
